import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { EntrataSession } from 'auth/EntrataSession';
import { RootState } from 'state/store';

import {
  ApplicantJourneyPageVisitPayload,
  trackApplicantJourneyPageVisit,
} from 'utils/applicantJourneyTracking';
import {
  buildTrackingKey,
  isLeadProfilePath,
  JourneyVisitIdentity,
  resolveJourneyVisitIdentity,
} from 'utils/applicantJourneyTrackingKey';

const DEDUPE_MS = 300;
const NOOP_CLEANUP = (): void => {};

interface JourneyVisitMetadata {
  pageType?: string;
  pageName?: string;
  companyUserId?: number | null;
  companyUserDisplayName?: string | null;
}

function buildEnterPayload(
  identity: JourneyVisitIdentity,
  metadata: JourneyVisitMetadata,
): ApplicantJourneyPageVisitPayload {
  return {
    sessionOrigin: identity.sessionOrigin,
    responseId: identity.responseId,
    applicationId: identity.applicationId,
    orgId: identity.orgId,
    propertyId: identity.propertyId,
    pageId: identity.pageId,
    pageType: metadata.pageType,
    pageName: metadata.pageName,
    action: 'enter',
    routePath: identity.pathname,
    companyUserId: metadata.companyUserId ?? undefined,
    companyUserDisplayName: metadata.companyUserDisplayName ?? undefined,
    navigationSource: identity.navigationSource,
    visitedAt: new Date().toISOString(),
  };
}

export function useApplicantJourneyTracking(): void {
  const location = useLocation();
  const { pageId: routePageId } = useParams();

  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isLeadProfile = useSelector((state: RootState) => state.auth.isLeadProfile);
  const isPreviewMode = useSelector((state: RootState) => state.auth.isPreviewMode);
  const companyUserId = useSelector((state: RootState) => state.auth.companyUserId);
  const companyUserDisplayName = useSelector(
    (state: RootState) => state.auth.companyUserDisplayName,
  );

  const applicant = useSelector((state: RootState) => state.application.applicant);
  const activeResponseId = useSelector(
    (state: RootState) => state.application.activeResponse?.responseId,
  );
  const activeApplicationId = useSelector(
    (state: RootState) => state.application.activeResponse?.applicationId,
  );
  const formPages = useSelector((state: RootState) => state.application.form?.pages);

  const leadProfileApplicationId = useSelector(
    (state: RootState) => state.leadProfile.applicationForm?.applicationId,
  );
  const leadProfilePages = useSelector(
    (state: RootState) => state.leadProfile.applicationForm?.pages,
  );
  const leadProfileApplicants = useSelector((state: RootState) => state.leadProfile.applicants);

  const currentTabIndex = useSelector((state: RootState) => state.navigation.currentTabIndex);
  const activeStepForTab = useSelector(
    (state: RootState) => state.navigation.activeStepByTab[state.navigation.currentTabIndex],
  );
  const leadProfileResponseId = useSelector(
    (state: RootState) => state.navigation.leadProfileResponseId,
  );

  const activeStepByTab = useMemo(
    () => ({ [currentTabIndex]: activeStepForTab ?? '' }),
    [activeStepForTab, currentTabIndex],
  );

  const activeResponse = useMemo(
    () =>
      activeResponseId || activeApplicationId
        ? { responseId: activeResponseId, applicationId: activeApplicationId }
        : undefined,
    [activeApplicationId, activeResponseId],
  );

  const visitIdentity = useMemo(
    () =>
      resolveJourneyVisitIdentity({
        pathname: location.pathname,
        routePageId,
        isPreviewMode,
        isAuthenticated,
        isLeadProfile,
        sessionOrigin: EntrataSession.getSessionOrigin(),
        sessionApplicant: EntrataSession.applicant ?? applicant,
        currentTabIndex,
        activeStepByTab,
        leadProfileResponseId,
        leadProfileApplicants,
        activeResponse,
        leadProfileApplicationId,
      }),
    [
      activeResponse,
      activeStepByTab,
      applicant,
      currentTabIndex,
      isAuthenticated,
      isLeadProfile,
      isPreviewMode,
      leadProfileApplicants,
      leadProfileApplicationId,
      leadProfileResponseId,
      location.pathname,
      routePageId,
    ],
  );

  const trackingKey = useMemo(
    () => (visitIdentity ? buildTrackingKey(visitIdentity) : null),
    [visitIdentity],
  );

  const onLeadProfileRoute = isLeadProfilePath(location.pathname);
  const pages =
    isLeadProfile || onLeadProfileRoute ? (leadProfilePages ?? formPages ?? []) : (formPages ?? []);
  const matchedPage = pages.find((page) => page.id === visitIdentity?.pageId);

  const metadataRef = useRef<JourneyVisitMetadata>({});
  metadataRef.current = {
    pageType: matchedPage?.type,
    pageName: matchedPage?.name,
    companyUserId,
    companyUserDisplayName,
  };

  const visitIdentityRef = useRef<JourneyVisitIdentity | null>(null);
  visitIdentityRef.current = visitIdentity;

  const lastEnterKeyRef = useRef<string | null>(null);
  const lastEnterAtRef = useRef<number>(0);
  const activePagePayloadRef = useRef<ApplicantJourneyPageVisitPayload | null>(null);
  const activeTrackingKeyRef = useRef<string | null>(null);
  const sentLeaveForKeyRef = useRef<string | null>(null);

  const trackLeaveOnce = (
    leaveTrackingKey: string,
    enterPayload: ApplicantJourneyPageVisitPayload,
  ): void => {
    if (sentLeaveForKeyRef.current === leaveTrackingKey) {
      return;
    }

    sentLeaveForKeyRef.current = leaveTrackingKey;
    trackApplicantJourneyPageVisit({
      ...enterPayload,
      action: 'leave',
      visitedAt: new Date().toISOString(),
    });
  };

  if (trackingKey && visitIdentity) {
    activePagePayloadRef.current = buildEnterPayload(visitIdentity, metadataRef.current);
    activeTrackingKeyRef.current = trackingKey;
  }

  useEffect(() => {
    const identity = visitIdentityRef.current;
    if (!trackingKey || !identity) {
      return NOOP_CLEANUP;
    }

    const enterPayload = buildEnterPayload(identity, metadataRef.current);
    const dedupeKey = `enter:${trackingKey}`;
    const now = Date.now();
    if (lastEnterKeyRef.current === dedupeKey && now - lastEnterAtRef.current < DEDUPE_MS) {
      return NOOP_CLEANUP;
    }

    lastEnterKeyRef.current = dedupeKey;
    lastEnterAtRef.current = now;
    sentLeaveForKeyRef.current = null;
    activePagePayloadRef.current = enterPayload;
    activeTrackingKeyRef.current = trackingKey;

    trackApplicantJourneyPageVisit(enterPayload);

    return () => {
      trackLeaveOnce(trackingKey, enterPayload);
    };
  }, [trackingKey]);

  useEffect(() => {
    const handlePageHide = () => {
      const leaveTrackingKey = activeTrackingKeyRef.current;
      const activePayload = activePagePayloadRef.current;
      if (!leaveTrackingKey || !activePayload) {
        return;
      }

      trackLeaveOnce(leaveTrackingKey, activePayload);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);
}
